import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useDataProvider } from 'react-admin';
import { GiftProduct, GiftProductType, FuelDiscountCostValueType } from '../../../../models';
import { getChainId } from '../../../../lib';
import { formatNumber } from '../../../../utils';

interface RewardDiscount {
  id: string;
  identify: string;
  description?: string;
  fuelDiscounts: {
    id: string;
    fuelId: string;
    fuel?: { name: string };
    alterPriceValue: number;
    alterPriceValueType: string;
    alterPriceType: string;
    costValue?: number;
    costValueType?: string;
  }[];
  places?: { id: string; name: string }[];
}

interface FuelDiscountProductFormProps {
  product: Partial<GiftProduct>;
  onChange: (product: Partial<GiftProduct>) => void;
  fuels: any[];
}

const FuelDiscountProductForm: React.FC<FuelDiscountProductFormProps> = ({
  product,
  onChange,
  fuels,
}) => {
  const dataProvider = useDataProvider();
  const chainId = getChainId();

  const [loading, setLoading] = useState(true);
  const [rewardDiscounts, setRewardDiscounts] = useState<RewardDiscount[]>([]);

  useEffect(() => {
    const fetchRewardDiscounts = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.getList('discounts', {
          filter: {
            type: 'reward',
            isEnabled: true,
            chainId,
          },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'createdAt', order: 'DESC' },
        });
        setRewardDiscounts(data || []);
      } catch (error) {
        console.error('Error fetching reward discounts:', error);
        setRewardDiscounts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRewardDiscounts();
  }, [dataProvider, chainId]);

  const handleDiscountSelect = (discountId: string) => {
    const selectedDiscount = rewardDiscounts.find(d => d.id === discountId);

    onChange({
      ...product,
      type: GiftProductType.FuelDiscount,
      discountId,
      // Keep these fields null/undefined - config comes from Discount
      fuelId: null,
      alterPriceValue: null,
      alterPriceValueType: null,
      alterPriceType: null,
      costValue: 0, // Gift discounts don't charge extra points
      costValueType: FuelDiscountCostValueType.points,
    });
  };

  const selectedDiscount = rewardDiscounts.find(d => d.id === product.discountId);

  const formatDiscountValue = (fd: RewardDiscount['fuelDiscounts'][0]) => {
    if (fd.alterPriceValueType === 'percentage') {
      return `${fd.alterPriceValue}%`;
    }
    if (fd.alterPriceValueType === 'cents') {
      return formatNumber(fd.alterPriceValue, { style: 'currency', currency: 'BRL' }) + '/L';
    }
    if (fd.alterPriceValueType === 'prefixed') {
      return `Fixo: ${formatNumber(fd.alterPriceValue, { style: 'currency', currency: 'BRL' })}/L`;
    }
    return `${fd.alterPriceValue}`;
  };

  const getFuelName = (fuelId: string, fuelData?: { name: string }) => {
    if (fuelData?.name) return fuelData.name;
    const fuel = fuels.find(f => f.id === fuelId);
    return fuel?.name || 'Combustível';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={32} />
        <Typography ml={2} color="text.secondary">
          Carregando descontos...
        </Typography>
      </Box>
    );
  }

  if (rewardDiscounts.length === 0) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <AlertTitle>Nenhum desconto do tipo Recompensa cadastrado</AlertTitle>
        <Typography variant="body2" mb={1}>
          Para criar um gift com desconto no combustível, você precisa primeiro cadastrar um desconto do tipo "Recompensa".
        </Typography>
        <Link to="/discounts/create" style={{ color: 'inherit', fontWeight: 600 }}>
          → Cadastrar novo desconto
        </Link>
      </Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl fullWidth required>
            <InputLabel>Desconto Vinculado</InputLabel>
            <Select
              value={product.discountId || ''}
              onChange={(e) => handleDiscountSelect(e.target.value as string)}
              label="Desconto Vinculado"
            >
              {rewardDiscounts.map((discount) => (
                <MenuItem key={discount.id} value={discount.id}>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {discount.identify}
                      {discount.description && ` - ${discount.description}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {discount.fuelDiscounts?.length || 0} combustível(is) configurado(s)
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Preview do desconto selecionado */}
        {selectedDiscount && (
          <Grid size={12}>
            <Box p={2} bgcolor="primary.lighter" borderRadius={2}>
              <Typography variant="subtitle2" color="primary.main" gutterBottom>
                Combustíveis disponíveis neste desconto:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedDiscount.fuelDiscounts?.map((fd) => (
                  <Chip
                    key={fd.id}
                    label={`${getFuelName(fd.fuelId, fd.fuel)}: ${formatDiscountValue(fd)}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
              {selectedDiscount.fuelDiscounts?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum combustível configurado neste desconto.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                O cliente poderá escolher qualquer combustível acima no momento do abastecimento.
              </Typography>

              {/* Postos configurados no desconto */}
              {selectedDiscount.places && selectedDiscount.places.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>
                    Postos onde o desconto pode ser usado:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedDiscount.places.map((place) => (
                      <Chip
                        key={place.id}
                        label={place.name}
                        size="small"
                        variant="outlined"
                        sx={{ bgcolor: 'grey.100' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FuelDiscountProductForm;
